import { ExEmptyState, ExIcon, ExInput, SearchFlavor, SearchSize } from '@boomi/exosphere';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import React from 'react';
import { translations } from '../translations/translations';
import type { Action, AuthenticationStatusResponse, Connector, Trigger } from '../types/types';
import IconCircle from './IconCircle';

import './task-item-picker.css';
import type { Language } from '../types/translations';

enum ItemTypeIs {
    Connector = 'connector',
    Trigger = 'trigger',
    Action = 'action',
}

interface Props {
    connectors: Record<string, Connector>;
    authenticatedConnectorsIds?: string[];
    pickerItems: (Connector | Trigger | Action)[];
    isMultiSelect: boolean;
    selectedItems: (Connector | Trigger | Action)[]; // array because we could have multiselect on
    onSelect: (item: Trigger | Action | Connector) => void;
    language: Language;
    authenticationStatus?: AuthenticationStatusResponse;
}

const TaskItemPicker = ({
    connectors,
    authenticatedConnectorsIds,
    pickerItems,
    isMultiSelect,
    selectedItems,
    onSelect,
    language = 'en',
}: Props) => {
    const sliderUUID = useRef(crypto.randomUUID());
    const [search, setSearch] = useState('');

    let sectionTitle = '';

    // Don't render anything if we have no data.
    if (pickerItems.length === 0) {
        return null;
    }

    // Figure out what type of items we are working with.
    const sampleItem = pickerItems[0];
    const sampleItemParentConnector = connectors[(sampleItem as Trigger | Action).providerId];
    const currentItemType =
        Object.hasOwn(sampleItem, 'triggers') && Object.hasOwn(sampleItem, 'actions')
            ? ItemTypeIs.Connector
            : sampleItemParentConnector.triggers[sampleItem.id]
              ? ItemTypeIs.Trigger
              : ItemTypeIs.Action; // if none of the above, then it can only be an Action

    const currentItemTypeIsConnector = currentItemType === ItemTypeIs.Connector;

    const listClassNames = classNames('task-item-picker-list', {
        'connector-grid': currentItemTypeIsConnector,
    });

    // If items are Connectors then filter/sort by "displayName" otherwise by "name" property.
    const getSortProperty = (item: Connector | Trigger | Action) =>
        (currentItemTypeIsConnector ? 'displayName' : 'name') as keyof typeof item;

    const filteredItems = pickerItems.filter((item) =>
        item[getSortProperty(item)]!.toUpperCase().includes(search.toUpperCase()),
    );

    const pendoClasses = classNames('new-task-pendo-request', {
        show: !filteredItems.length,
    });

    const getUniqueItems = (items: (Connector | Trigger | Action)[]) => {
        const unique = [...new Map(items.map((item) => [item.id, item])).values()];
        return unique.sort((a, b) => a[getSortProperty(a)]!.localeCompare(b[getSortProperty(b)]!));
    };

    const generateItemTile = (item: Connector | Trigger | Action) => {
        const itemId = `${currentItemType}_${item.id}_${sliderUUID.current}`;
        const itemClasses = classNames('app-tile', {
            'connector-tile': currentItemTypeIsConnector,
            'trigger-or-action-tile': !currentItemTypeIsConnector,
        });
        const itemTooltip = currentItemTypeIsConnector
            ? item.description
            : (item as Trigger | Action).name;
        const itemTitle = currentItemTypeIsConnector
            ? (item as Connector).displayName
            : (item as Trigger | Action).name;
        const itemConnector = currentItemTypeIsConnector
            ? item
            : connectors[(item as Trigger | Action).providerId];

        return (
            <label className={itemClasses} title={itemTooltip} htmlFor={itemId}>
                <IconCircle
                    connector={itemConnector as Connector}
                    showAuthBadge={
                        currentItemTypeIsConnector &&
                        !!authenticatedConnectorsIds?.includes(item.id)
                    }
                    language={language}
                />
                <span className="app-tile-text">{itemTitle}</span>
                <input
                    id={itemId}
                    className="app-tile-input"
                    type={isMultiSelect ? 'checkbox' : 'radio'}
                    onChange={() => onSelect(item)}
                    name={`taskItemSet_${sliderUUID.current}`}
                    checked={selectedItems?.some((selected) => selected.id === item.id)}
                />
            </label>
        );
    };

    const itemsToDisplay =
        !!search && selectedItems?.length
            ? getUniqueItems([...selectedItems, ...filteredItems]) // show any already selected items when searching
            : filteredItems;

    return (
        <section className="task-item-picker">
            <ExInput
                data-testId="taskItemPickerSearchInput"
                className="exo-search-input"
                placeholder={translations[language].COMMON_search_placeholder}
                input-id="default"
                type="text"
                inputmode="text"
                size={SearchSize.MEDIUM}
                flavor={SearchFlavor.WHITE}
                leadingIcon="magnifying-glass"
                onChange={(e) => setSearch((e.currentTarget as HTMLInputElement).value)}
                autoComplete="off"
                clearable
            />
            <div className="task-item-picker-container custom-scrollbars">
                {itemsToDisplay.length ? (
                    <ul className={listClassNames} data-testid={`${currentItemType}List`}>
                        {itemsToDisplay.map((item) => {
                            const newListItem = (
                                <React.Fragment key={item.id}>
                                    {/* For triggers and actions show a section title, based on parent connector. */}
                                    {!currentItemTypeIsConnector &&
                                    (item as Trigger | Action).providerId !== sectionTitle ? (
                                        <h3
                                            key={`section_${item.id}`}
                                            className="text md list-section"
                                        >
                                            {
                                                connectors[(item as Trigger | Action).providerId]
                                                    .displayName
                                            }
                                        </h3>
                                    ) : null}
                                    {/* The actual connector/trigger/action list tile */}
                                    <li key={item.id}>{generateItemTile(item)}</li>
                                </React.Fragment>
                            );

                            // Update section title when we start showing triggers/action from another connector.
                            if (
                                !currentItemTypeIsConnector &&
                                (item as Trigger | Action).providerId !== sectionTitle
                            ) {
                                sectionTitle = (item as Trigger | Action).providerId;
                            }

                            return newListItem;
                        })}
                    </ul>
                ) : (
                    <div className="empty-state-wrapper">
                        <ExEmptyState
                            className="empty-state"
                            label={translations[language].COMMON_no_results}
                            text={translations[language].COMMON_search_hint}
                        >
                            <ExIcon className="empty-state-icon" slot="header" icon="community" />
                        </ExEmptyState>
                    </div>
                )}
            </div>
            <section className={pendoClasses}>
                <span className="send-request-text text sm">
                    {translations[language].TASK_BUILDER_send_request_before}
                    <span id="send-a-request" className="btn-text">
                        {translations[language].TASK_BUILDER_send_request}
                    </span>
                    {translations[language].TASK_BUILDER_send_request_after}
                </span>
            </section>
        </section>
    );
};
export default TaskItemPicker;
